import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import logo from "../assets/logo.png";
import image3 from "../assets/image_3.jpg";

export default function Home() {
  const [currentWord, setCurrentWord] = useState(0);
  const words = ["CAN", "WILL", "MUST"];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentWord((prev) => (prev + 1) % words.length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const slideInLeft = {
    hidden: { opacity: 0, x: -100 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 1,
        ease: "easeOut",
      },
    },
  };

  const slideInRight = {
    hidden: { opacity: 0, x: 100 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 1,
        ease: "easeOut",
      },
    },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const wordVariants = {
    enter: {
      opacity: 0,
      y: 50,
      scale: 0.8,
    },
    center: {
      opacity: 1,
      y: 0,
      scale: 1,
    },
    exit: {
      opacity: 0,
      y: -50,
      scale: 0.8,
    },
  };

  return (
    <div className="w-full bg-gray-900 text-white overflow-x-hidden">
      {/* Hero Section - Image Left, Content Right */}
      <motion.section 
        className="relative min-h-screen flex items-center bg-gradient-to-br from-gray-900 via-gray-800 to-black"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 xl:gap-16 2xl:gap-20 items-center min-h-screen py-16 lg:py-0">
            
            {/* Image Section - Left */}
            <motion.div 
              className="order-2 lg:order-1 flex justify-center lg:justify-start"
              variants={slideInLeft}
            >
              <motion.div 
                className="relative group max-w-full"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <motion.img
                  src={image3}
                  alt="Nutrition & Wellness"
                  className="w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-full xl:max-w-2xl h-auto object-cover rounded-3xl shadow-2xl border-4 border-gray-700 group-hover:border-[#fded22] transition-all duration-300"
                  whileHover={{ 
                    borderColor: "#fded22",
                    boxShadow: "0px 0px 40px rgba(253,237,34,0.4)"
                  }}
                />
                <motion.div
                  className="absolute inset-0 rounded-3xl bg-gradient-to-br from-[#fded22]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                />
              </motion.div>
            </motion.div>

            {/* Content Section - Right */}
            <motion.div 
              className="order-1 lg:order-2 text-center lg:text-left space-y-6 lg:space-y-8 xl:space-y-10"
              variants={slideInRight}
            >
              {/* Main Brand Title */}
              <motion.div variants={itemVariants}>
                <motion.h1 
                  className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl 2xl:text-8xl font-black mb-4 lg:mb-6 tracking-tight leading-tight"
                  whileHover={{ 
                    scale: 1.02,
                    textShadow: "0px 0px 30px rgba(253,237,34,0.8)"
                  }}
                  transition={{ duration: 0.3 }}
                >
                  <span className="bg-gradient-to-r from-white via-yellow-200 to-[#fded22] bg-clip-text text-transparent">
                    KLIMKAARA
                  </span>
                </motion.h1>
                <motion.h2 
                  className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-300 mb-6 lg:mb-8"
                  variants={itemVariants}
                >
                  FITNESS CENTER
                </motion.h2>
              </motion.div>

              {/* Animated Text - You Can/Will/Must */}
              <motion.div 
                className="space-y-4 lg:space-y-6"
                variants={itemVariants}
              >
                <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-black">
                  <motion.span 
                    className="text-white mr-4 lg:mr-6"
                    whileHover={{ color: "#fded22" }}
                  >
                    YOU
                  </motion.span>
                  <AnimatePresence mode="wait">
                    <motion.span
                      key={currentWord}
                      variants={wordVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      transition={{ duration: 0.5 }}
                      className="text-[#fded22] inline-block"
                    >
                      {words[currentWord]}
                    </motion.span>
                  </AnimatePresence>
            </div>
                
                <motion.div 
                  className="h-1 w-16 sm:w-20 lg:w-24 xl:w-32 bg-gradient-to-r from-[#fded22] to-yellow-500 mx-auto lg:mx-0"
                  initial={{ width: 0 }}
                  animate={{ width: "auto" }}
                  transition={{ delay: 0.8, duration: 0.8 }}
                />
              </motion.div>

              {/* Description */}
              <motion.p 
                className="text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl text-gray-300 leading-relaxed max-w-2xl mx-auto lg:mx-0"
                variants={itemVariants}
                whileHover={{ color: "#d1d5db" }}
              >
                Transform your body, elevate your mind, and achieve the impossible. 
                Your fitness journey starts here with professional guidance and unwavering support.
              </motion.p>

              {/* Call to Action Buttons */}
              <motion.div 
                className="flex flex-col sm:flex-row gap-4 lg:gap-6 justify-center lg:justify-start"
                variants={itemVariants}
              >
                <motion.a 
                  href="#services" 
                  className="inline-block bg-gradient-to-r from-[#fded22] to-yellow-500 text-gray-900 px-6 sm:px-8 lg:px-10 xl:px-12 py-3 sm:py-4 lg:py-5 rounded-full text-base sm:text-lg lg:text-xl xl:text-2xl font-black uppercase tracking-wider shadow-2xl text-center"
                  whileHover={{ 
                    scale: 1.05, 
                    boxShadow: "0px 15px 40px rgba(253,237,34,0.5)",
                    y: -5
                  }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                >
                  OUR SERVICES
                </motion.a>
                <motion.a 
                  href="#testimonials" 
                  className="inline-block border-2 border-[#fded22] text-[#fded22] px-6 sm:px-8 lg:px-10 xl:px-12 py-3 sm:py-4 lg:py-5 rounded-full text-base sm:text-lg lg:text-xl xl:text-2xl font-black uppercase tracking-wider hover:bg-[#fded22] hover:text-gray-900 transition-all duration-300 text-center"
                  whileHover={{ 
                    scale: 1.05,
                    backgroundColor: "#fded22",
                    color: "#111827"
                  }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                >
                  SUCCESS STORIES
                </motion.a>
              </motion.div>
            </motion.div>
            </div>
        </div>

        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5 pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-br from-transparent via-[#fded22] to-transparent transform rotate-45 scale-150"></div>
        </div>
      </motion.section>

      {/* Services Section - Gym-focused Grid */}
      <motion.section 
        id="services" 
        className="py-12 sm:py-16 lg:py-20 xl:py-24 bg-gradient-to-b from-black to-gray-900"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={containerVariants}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16">
          <motion.div className="text-center mb-8 sm:mb-12 lg:mb-16 xl:mb-20" variants={itemVariants}>
            <motion.h2 
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black mb-4 lg:mb-6"
              whileHover={{ scale: 1.02, color: "#fded22" }}
              transition={{ duration: 0.3 }}
            >
              OUR <span className="text-[#fded22]">SERVICES</span>
            </motion.h2>
            <motion.div 
              className="h-1 w-24 sm:w-32 lg:w-48 xl:w-64 bg-gradient-to-r from-[#fded22] to-yellow-500 mx-auto mb-4 lg:mb-6"
              initial={{ width: 0 }}
              whileInView={{ width: "auto" }}
              transition={{ duration: 0.8 }}
            />
            <motion.p 
              className="text-lg sm:text-xl lg:text-2xl xl:text-3xl text-gray-300 max-w-4xl mx-auto"
              variants={itemVariants}
            >
              Professional training programs designed to push your limits
            </motion.p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 xl:gap-10"
            variants={staggerContainer}
          >
            {[
              {
                title: "PERSONAL TRAINING",
                description: "One-on-one elite coaching sessions",
                icon: "💪"
              },
              {
                title: "NUTRITION COACHING",
                description: "Customized meal plans for peak performance",
                icon: "🥗"
              },
              {
                title: "YOGA & FLEXIBILITY",
                description: "Mind-body connection and mobility",
                icon: "🧘"
              },
              {
                title: "HORMONE OPTIMIZATION",
                description: "Specialized thyroid & hormone support",
                icon: "⚡"
              },
              {
                title: "CARDIO CONDITIONING",
                description: "High-intensity cardiovascular training",
                icon: "🏃"
              },
              {
                title: "STRENGTH BUILDING",
                description: "Power and muscle development programs",
                icon: "🏋️"
              }
            ].map((service, index) => (
              <motion.div 
                key={index}
                className="group bg-gradient-to-br from-gray-800 to-gray-900 p-4 sm:p-6 lg:p-8 xl:p-10 rounded-2xl border border-gray-700 hover:border-[#fded22] transition-all duration-300 cursor-pointer"
                variants={itemVariants}
                whileHover={{ 
                  scale: 1.03, 
                  y: -10,
                  boxShadow: "0px 20px 40px rgba(253,237,34,0.2)"
                }}
                transition={{ duration: 0.3 }}
              >
                <motion.div 
                  className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl mb-3 sm:mb-4 lg:mb-6"
                  whileHover={{ scale: 1.2, rotate: 10 }}
                  transition={{ duration: 0.3 }}
                >
                  {service.icon}
                </motion.div>
                <motion.h3 
                  className="text-lg sm:text-xl lg:text-2xl xl:text-3xl font-black mb-2 sm:mb-3 lg:mb-4 text-white group-hover:text-[#fded22] transition-colors duration-300"
                  whileHover={{ x: 5 }}
                >
                  {service.title}
                </motion.h3>
                <motion.p 
                  className="text-gray-400 group-hover:text-gray-300 transition-colors duration-300 text-sm sm:text-base lg:text-lg xl:text-xl"
                  whileHover={{ x: 5 }}
                >
                  {service.description}
                </motion.p>
                <motion.div 
                  className="mt-3 sm:mt-4 lg:mt-6 h-1 w-0 bg-gradient-to-r from-[#fded22] to-yellow-500 group-hover:w-full transition-all duration-500"
                />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.section>

      {/* Testimonials Section - Enhanced Layout */}
      <motion.section 
        id="testimonials" 
        className="py-12 sm:py-16 lg:py-20 xl:py-24 bg-gradient-to-b from-gray-900 to-black"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={containerVariants}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16">
          <motion.div className="text-center mb-8 sm:mb-12 lg:mb-16 xl:mb-20" variants={itemVariants}>
            <motion.h2 
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black mb-4 lg:mb-6"
              whileHover={{ scale: 1.02, color: "#fded22" }}
              transition={{ duration: 0.3 }}
            >
              SUCCESS <span className="text-[#fded22]">STORIES</span>
            </motion.h2>
            <motion.div 
              className="h-1 w-24 sm:w-32 lg:w-48 xl:w-64 bg-gradient-to-r from-[#fded22] to-yellow-500 mx-auto mb-4 lg:mb-6"
              initial={{ width: 0 }}
              whileInView={{ width: "auto" }}
              transition={{ duration: 0.8 }}
            />
            <motion.p 
              className="text-lg sm:text-xl lg:text-2xl xl:text-3xl text-gray-300 max-w-4xl mx-auto"
              variants={itemVariants}
            >
              Real transformations from our dedicated members
            </motion.p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 xl:gap-10"
            variants={staggerContainer}
          >
            {[
              {
                quote: "If you're looking for a gym that motivates you to achieve your goals while making fitness enjoyable, Klimkaara is the place to be.A special thanks to the amazing trainers—Chaitanya sir, Mohan sir, Hari Krishna sir, and Roopa Ma'am—for their constant motivation and support. Their dedication and encouragement have truly made my fitness journey enjoyable and effective.",
                author: "Bhanu Prakash",
                role: "Transformed in 6 months",
                rating: 5
              },
              {
                quote: "I was surprised by the quality of the equipment and the expertise of the trainers. The prices are very reasonable, and I feel like I'm getting great value for my money. It's a great atmosphere to work out in. Trainers are well friendly you'll get know best workout schedule and proper diet.",
                author: "Kesana Soumya Sri",
                role: "Transformed in 3 months",
                rating: 5
              },
              {
                quote: "As my Personal Experience, I have Lost 5kgs weight in 45 days With a Healthy diet. Well trained Staff, Good Equipment. Overall, Topnotch in my experience.",
                author: "Prabhu Bonthala",
                role: "Transformed in 45 days",
                rating: 5
              }
            ].map((testimonial, index) => (
              <motion.div 
                key={index}
                className="bg-gradient-to-br from-gray-800 to-gray-900 p-4 sm:p-6 lg:p-8 xl:p-10 rounded-2xl border border-gray-700 hover:border-[#fded22] transition-all duration-300"
                variants={itemVariants}
                whileHover={{ 
                  scale: 1.03,
                  y: -5,
                  boxShadow: "0px 15px 35px rgba(253,237,34,0.15)"
                }}
                transition={{ duration: 0.3 }}
              >
                <motion.div 
                  className="flex mb-3 sm:mb-4"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <motion.span 
                      key={i}
                      className="text-[#fded22] text-lg sm:text-xl"
                      initial={{ scale: 0 }}
                      whileInView={{ scale: 1 }}
                      transition={{ delay: 0.1 * i }}
                    >
                      ⭐
                    </motion.span>
                  ))}
                </motion.div>
                <motion.p 
                  className="text-gray-300 mb-4 sm:mb-6 text-sm sm:text-base lg:text-lg xl:text-xl italic leading-relaxed"
                  whileHover={{ color: "#d1d5db" }}
                >
                  "{testimonial.quote}"
                </motion.p>
                <motion.div 
                  className="border-t border-gray-700 pt-3 sm:pt-4"
                  whileHover={{ borderColor: "#fded22" }}
                >
                  <motion.h4 
                    className="font-bold text-base sm:text-lg lg:text-xl text-[#fded22]"
                    whileHover={{ scale: 1.05 }}
                  >
                    {testimonial.author}
                  </motion.h4>
                  <motion.p 
                    className="text-gray-400 text-xs sm:text-sm lg:text-base"
                    whileHover={{ color: "#9ca3af" }}
                  >
                    {testimonial.role}
                  </motion.p>
                </motion.div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.section>
    </div>
  );
}
